// Company AI Enhancement Panel Component
import React, { useState } from 'react';
import { 
  CpuChipIcon, 
  ArrowTrendingUpIcon,
  BuildingOfficeIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline';

const CompanyAIPanel = ({ company }) => {
  const [showDetailedAnalysis, setShowDetailedAnalysis] = useState(false);

  // Solo se renderiza con un análisis real: al menos un score numérico > 0.
  // Datos del simulador viejo (todo 0 / strings tipo "Média/100") y null quedan ocultos;
  // cuando el enriquecimiento real de empresas exista (visión #5), el panel revive solo.
  const analysis = company?.ai_company_analysis;
  const hasRealAnalysis = analysis && typeof analysis === 'object' &&
    [analysis.industry_fit, analysis.digital_maturity, analysis.size_compatibility, analysis.revenue_compatibility]
      .some((v) => Number(v) > 0);
  if (!hasRealAnalysis) return null;

  const companyAnalysis = analysis;
  const industryFit = companyAnalysis.industry_fit || 0;
  const budgetCapacity = companyAnalysis.budget_capacity || 'Não analisado';
  const digitalMaturity = companyAnalysis.digital_maturity || 0;
  const sizeCompatibility = companyAnalysis.size_compatibility || 0;
  const revenueCompatibility = companyAnalysis.revenue_compatibility || 0;
  const painPoints = companyAnalysis.pain_points_identified || [];

  const getScoreColor = (score) => {
    if (score >= 70) return 'text-green-600';
    if (score >= 40) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBg = (score) => {
    if (score >= 70) return 'bg-green-100';
    if (score >= 40) return 'bg-yellow-100';
    return 'bg-red-100';
  };

  const getCompatibilityLevel = (score) => {
    if (score >= 70) return { label: 'Alta', icon: CheckCircleIcon, color: 'text-green-600' };
    if (score >= 40) return { label: 'Média', icon: ExclamationTriangleIcon, color: 'text-yellow-600' };
    return { label: 'Baixa', icon: XCircleIcon, color: 'text-red-600' };
  };

  const overallCompatibility = Math.round((industryFit + sizeCompatibility + revenueCompatibility) / 3);
  const compatibility = getCompatibilityLevel(overallCompatibility);
  const CompatibilityIcon = compatibility.icon;

  return (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-100 border border-blue-200 rounded-lg p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <CpuChipIcon className="h-6 w-6 text-white" />
            </div>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">AI Company Analysis</h3>
            <p className="text-sm text-blue-600">Powered by AI Research Agent v2.0</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <div className="text-right">
            <div className="text-xs text-gray-500">Status</div>
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span className="text-sm font-medium text-green-600">ENHANCED</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* Industry Fit */}
        <div className="bg-white rounded-lg p-4 border">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs font-medium text-gray-500 uppercase">Industry Fit</div>
              <div className={`text-2xl font-bold ${getScoreColor(industryFit)}`}>
                {industryFit}/100
              </div>
            </div>
            <div className={`w-12 h-12 ${getScoreBg(industryFit)} rounded-lg flex items-center justify-center`}>
              <BuildingOfficeIcon className={`h-6 w-6 ${getScoreColor(industryFit)}`} />
            </div>
          </div>
        </div>

        {/* Digital Maturity */}
        <div className="bg-white rounded-lg p-4 border">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs font-medium text-gray-500 uppercase">Digital Maturity</div>
              <div className={`text-2xl font-bold ${getScoreColor(digitalMaturity)}`}>
                {digitalMaturity}/100
              </div>
            </div>
            <div className={`w-12 h-12 ${getScoreBg(digitalMaturity)} rounded-lg flex items-center justify-center`}>
              <ChartBarIcon className={`h-6 w-6 ${getScoreColor(digitalMaturity)}`} />
            </div>
          </div>
        </div>

        {/* Overall Compatibility */}
        <div className="bg-white rounded-lg p-4 border">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs font-medium text-gray-500 uppercase">Compatibility</div>
              <div className={`text-lg font-bold ${compatibility.color}`}>
                {compatibility.label}
              </div>
              <div className="text-xs text-gray-500">{overallCompatibility}/100</div>
            </div>
            <div className={`w-12 h-12 ${getScoreBg(overallCompatibility)} rounded-lg flex items-center justify-center`}>
              <CompatibilityIcon className={`h-6 w-6 ${compatibility.color}`} />
            </div>
          </div>
        </div>
      </div>

      {/* Budget Capacity */}
      <div className="bg-white rounded-lg p-4 border mb-4">
        <div className="flex items-start space-x-3">
          <CurrencyDollarIcon className="h-5 w-5 text-blue-600 mt-0.5" />
          <div className="flex-1">
            <h4 className="font-medium text-gray-900 mb-1">Budget Capacity</h4>
            <p className="text-sm text-gray-700">{budgetCapacity}</p>
          </div>
        </div>
      </div>

      {/* Pain Points */}
      {painPoints.length > 0 && (
        <div className="bg-white rounded-lg p-4 border mb-4">
          <div className="flex items-start space-x-3">
            <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-medium text-gray-900 mb-2">Identified Pain Points</h4>
              <ul className="space-y-1">
                {painPoints.map((point, index) => (
                  <li key={index} className="text-sm text-gray-700 flex items-start">
                    <span className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Detailed Analysis Toggle */}
      <button
        onClick={() => setShowDetailedAnalysis(!showDetailedAnalysis)}
        className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
      >
        <span className="text-sm font-medium">
          {showDetailedAnalysis ? 'Hide' : 'Show'} Detailed AI Analysis
        </span>
        {showDetailedAnalysis ? (
          <ChevronUpIcon className="h-4 w-4" />
        ) : (
          <ChevronDownIcon className="h-4 w-4" />
        )}
      </button>

      {/* Detailed Analysis Panel */}
      {showDetailedAnalysis && (
        <div className="mt-4 space-y-4">
          {/* Compatibility Breakdown */}
          <div className="bg-white rounded-lg p-4 border">
            <h4 className="font-medium text-gray-900 mb-3">Compatibility Breakdown</h4>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Size Compatibility</span>
                <span className={`text-sm font-medium ${getScoreColor(sizeCompatibility)}`}>
                  {sizeCompatibility}/100
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Revenue Compatibility</span>
                <span className={`text-sm font-medium ${getScoreColor(revenueCompatibility)}`}>
                  {revenueCompatibility}/100
                </span>
              </div>
            </div>
          </div>

          {/* Additional Analysis */}
          {company.ai_combined_insights && (
            <div className="bg-white rounded-lg p-4 border">
              <h4 className="font-medium text-gray-900 mb-3">Strategic Insights</h4>
              <div className="text-sm text-gray-700">
                <p className="italic">
                  Ver análisis completo en el panel del contacto principal para insights detallados sobre oportunidades y riesgos.
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CompanyAIPanel;